import { getUserErrorMessage } from '@packages/shared';
import { useCallback, useEffect, useState } from 'react';

const APPLE_PAY_ENABLED = true;
const DEFAULT_APPLE_PAY_VERSION = 14;

export type ApplePayAuthorizationResult =
  | { status: 'SUCCESS' }
  | { status: 'FAILED'; errorMessage: string };

type ApplePayButtonProps = {
  paymentRequest: ApplePayPaymentRequest;
  validateMerchant: (validationURL: string) => Promise<ApplePayMerchantSession>;
  authorizePayment: (payment: ApplePayPayment) => Promise<ApplePayAuthorizationResult>;
  onSuccess: () => void;
  onError: (message: string) => void;
  onSubmitting?: () => void;
  onCancel?: () => void;
  version?: number;
};

const isApplePayAvailable = (version: number): boolean => {
  if (typeof ApplePaySession === 'undefined') {
    return false;
  }

  return ApplePaySession.supportsVersion(version) && ApplePaySession.canMakePayments();
};

export const ApplePayButton = ({
  paymentRequest,
  validateMerchant,
  authorizePayment,
  onSuccess,
  onError,
  onSubmitting,
  onCancel,
  version = DEFAULT_APPLE_PAY_VERSION,
}: ApplePayButtonProps) => {
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    if (!APPLE_PAY_ENABLED) return;
    try {
      setIsAvailable(isApplePayAvailable(version));
    } catch {
      setIsAvailable(false);
    }
  }, [version]);

  const handleClick = useCallback(() => {
    const session = new ApplePaySession(version, paymentRequest);

    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await validateMerchant(event.validationURL);
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        session.abort();
        onError(getUserErrorMessage(error));
      }
    };

    session.onpaymentauthorized = async (event) => {
      onSubmitting?.();

      try {
        const result = await authorizePayment(event.payment);

        if (result.status === 'SUCCESS') {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          onSuccess();
          return;
        }

        session.completePayment(ApplePaySession.STATUS_FAILURE);
        onError(result.errorMessage);
      } catch (error) {
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        onError(getUserErrorMessage(error));
      }
    };

    session.oncancel = () => {
      onCancel?.();
    };

    session.begin();
  }, [
    authorizePayment,
    onCancel,
    onError,
    onSubmitting,
    onSuccess,
    paymentRequest,
    validateMerchant,
    version,
  ]);

  if (!isAvailable) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        WebkitAppearance: '-apple-pay-button' as never,
        appearance: '-apple-pay-button' as never,
        width: '100%',
        height: '48px',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
      }}
    />
  );
};
